import clsx from 'clsx';
import { useAuth } from 'context/auth';
import { useCallback } from 'react';

import { Icon } from 'common/icon';
import { Button } from 'common/new-button';
import { useAlert } from 'components';
import {
  useToogleDislikeMutation,
  useToogleLikeMutation,
} from 'hooks/api/casino_reviews';
import { useLoginModal } from 'hooks';
import { LoginModal } from 'components/modal/login';
import { handleMutation } from 'utils/handleMutation';
import { invalidateQueryCache } from 'hooks/useQuery';
import { Analytics } from 'constants/analytics';

import styles from './styles.module.css';

export const ReviewsCardFooter = ({
  like,
  dislike,
  reviewId,
  isSlider = false,
  handleLoginModal,
}: {
  reviewId?: string;
  like: number;
  dislike: number;
  isSlider: boolean;
  handleLoginModal?: () => void;
}) => {
  const alert = useAlert();

  const { isLoggedIn } = useAuth();

  const likeText = `Yes (${like})`;
  const dislikeText = `No (${dislike})`;

  const [handleLike, likeMutation] = useToogleLikeMutation();
  const [handleDislike, dislikeMutation] = useToogleDislikeMutation();
  const {
    handleCloseLoginModal,
    handleConfirmLoginModal,
    handleVisibleLoginModal,
    isLoginVisible,
  } = useLoginModal();

  const onSuccessLike = useCallback(() => {
    invalidateQueryCache('casinoReviews');
    alert.success('Like successfully added');
  }, [alert]);

  const onErrorLike = useCallback(
    (error: any) => {
      alert.error(error?.data?.error || 'Error');
    },
    [alert]
  );

  const onSuccessDislike = useCallback(() => {
    invalidateQueryCache('casinoReviews');
    alert.success('Dislike successfully added');
  }, [alert]);

  const onErrorDislike = useCallback(
    (error: any) => {
      alert.error(error?.data?.error || 'Error');
    },
    [alert]
  );

  handleMutation({
    ...likeMutation,
    onSuccess: onSuccessLike,
    onError: onErrorLike,
  });

  handleMutation({
    ...dislikeMutation,
    onSuccess: onSuccessDislike,
    onError: onErrorDislike,
  });

  return (
    <div className={styles.casino_review__help}>
      <div className={styles.casino_review__help_question}>
        Was this review helpful?
      </div>

      <div className={styles.casino_review__help_btns}>
        <Button
          id={Analytics.reviews.like}
          text={likeText}
          icon={<Icon.Like />}
          className={styles.casino_review__help_btn}
          onClick={async () => {
            if (isLoggedIn) {
              if (reviewId) {
                await handleLike({ reviewId });
              }
            } else if (isSlider && handleLoginModal) {
              handleLoginModal();
            } else {
              handleVisibleLoginModal();
            }
          }}
        />
        <Button
          id={Analytics.reviews.dislike}
          text={dislikeText}
          icon={<Icon.Dislike />}
          className={styles.casino_review__help_btn}
          onClick={async () => {
            if (isLoggedIn) {
              if (reviewId) {
                await handleDislike({ reviewId });
              }
            } else if (isSlider && handleLoginModal) {
              handleLoginModal();
            } else {
              handleVisibleLoginModal();
            }
          }}
        />
      </div>

      {!isSlider && (
        <LoginModal
          isVisible={isLoginVisible}
          onClose={handleCloseLoginModal}
          confirmOnClick={handleConfirmLoginModal}
        />
      )}
    </div>
  );
};

export const RecommendStatus = ({
  recommend,
  haveDescription = true,
}: {
  recommend: boolean;
  haveDescription?: boolean;
}) => {
  const statusClasses = clsx(styles.casino_review__status, {
    [styles.casino_review_recommended]: recommend,
    [styles.casino_review_not_recommended]: !recommend,
  });

  return (
    <div className={statusClasses}>
      {recommend ? (
        <div className={statusClasses}>
          <Icon.LikeRecommend />
          {haveDescription && <span>Recommended</span>}
        </div>
      ) : (
        <div className={statusClasses}>
          <Icon.DislikeRecommend />
          {haveDescription && <span>Not Recommended</span>}
        </div>
      )}
    </div>
  );
};
